<template>
  <div class="report-page">
    <div class="page-shell">
      <header class="page-header">
        <div class="header-copy">
          <p class="eyebrow">AI 评分报告</p>
          <h1>考试结果分析</h1>
          <p class="header-subtitle">{{ examInfo.caseTitle || '加载考试数据后生成完整报告' }}</p>
        </div>

        <div class="header-actions">
          <button class="action-btn" @click="goBack">返回</button>
          <button class="action-btn" @click="reloadReport" :disabled="loading">
            {{ summary.aiAvailable === false ? '重新 AI 分析' : '刷新' }}
          </button>
          <button class="action-btn" @click="openReviewVideo" :disabled="reviewVideos.length === 0">复盘视频</button>
          <button class="action-btn primary" @click="downloadReport">打印报告</button>
        </div>
      </header>

      <div v-if="loading" class="state-panel">
        <div class="spinner"></div>
        <p>正在加载考试结果...</p>
      </div>

      <div v-else-if="errorMsg" class="state-panel state-error">
        <p>{{ errorMsg }}</p>
        <button class="action-btn primary" @click="reloadReport">重试</button>
      </div>

      <template v-else>
        <section class="section-band">
          <div class="summary-grid">
            <article class="summary-card score-card">
              <span class="card-label">总分</span>
              <div class="score-value">{{ summary.totalScore }}</div>
              <p class="card-note">综合 AI 评分结果</p>
            </article>

            <article class="summary-card">
              <span class="card-label">语义匹配</span>
              <div class="metric-value">{{ summary.semanticScore }}</div>
              <p class="card-note">与标准答案的语义贴合度</p>
            </article>

            <article class="summary-card">
              <span class="card-label">关键词匹配</span>
              <div class="metric-value">{{ summary.keywordScore }}</div>
              <p class="card-note">命中标准关键词的情况</p>
            </article>

            <article class="summary-card">
              <span class="card-label">内容质量</span>
              <div class="metric-value">{{ summary.qualityScore }}</div>
              <p class="card-note">完整性、逻辑性、专业性</p>
            </article>
          </div>
        </section>

        <section class="section-band">
          <div class="meta-grid">
            <article class="info-card">
              <h2>考试信息</h2>
              <dl class="info-list">
                <div><dt>考生</dt><dd>{{ currentUser.realName || currentUser.username || '学院端用户' }}</dd></div>
                <div><dt>案例</dt><dd>{{ examInfo.caseTitle || '未知案例' }}</dd></div>
                <div><dt>状态</dt><dd>{{ isExamCompleted ? '已完成' : '进行中' }}</dd></div>
                <div><dt>结束时间</dt><dd>{{ formatTime(examInfo.endTime || examInfo.createdTime) }}</dd></div>
              </dl>
            </article>

            <article class="info-card">
              <h2>匹配关键词</h2>
              <div v-if="matchedKeywords.length" class="tag-wrap">
                <span v-for="(keyword, index) in matchedKeywords" :key="index" class="tag">
                  {{ keyword }}
                </span>
              </div>
              <p v-else class="empty-text">暂无关键词命中</p>
            </article>
          </div>
        </section>

        <section class="section-band">
          <div class="analysis-grid">
            <article class="analysis-card">
              <h2>优势亮点</h2>
              <p class="text-block">{{ summary.advantages || '暂无分析' }}</p>
            </article>
            <article class="analysis-card">
              <h2>不足之处</h2>
              <p class="text-block">{{ summary.disadvantages || '暂无分析' }}</p>
            </article>
            <article class="analysis-card">
              <h2>改进建议</h2>
              <p class="text-block">{{ summary.suggestions || '暂无建议' }}</p>
            </article>
            <article class="analysis-card">
              <h2>下次培训重点</h2>
              <p class="text-block">{{ summary.trainingFocus || '暂无重点' }}</p>
            </article>
          </div>

          <article v-if="summary.potentialPublicOpinion" class="analysis-card single-card">
            <h2>可能产生的舆情</h2>
            <p class="text-block">{{ summary.potentialPublicOpinion }}</p>
          </article>
        </section>

        <section v-if="dimensionEntries.length" class="section-band">
          <div class="section-head">
            <h2>维度得分</h2>
            <p>每个维度按当前答案数据汇总</p>
          </div>

          <div class="dimension-list">
            <div v-for="item in dimensionEntries" :key="item.name" class="dimension-row">
              <div class="dimension-top">
                <span>{{ item.name }}</span>
                <span>{{ item.score }}</span>
              </div>
              <div class="dimension-bar">
                <span :style="{ width: item.percent + '%' }"></span>
              </div>
            </div>
          </div>
        </section>

        <section class="section-band">
          <div class="section-head">
            <h2>答题详情</h2>
            <p>展示每次回答的文字、语音和 AI 分析</p>
          </div>

          <div v-if="answers.length === 0" class="state-panel compact">
            <p>暂无答题记录</p>
          </div>

          <article v-for="answer in answers" :key="answer.id" class="answer-card">
            <div class="answer-head">
              <div>
                <h3>第 {{ answer.index }} 题</h3>
                <p>{{ answer.videoTitle || '未绑定视频' }}</p>
              </div>
              <div class="answer-score">{{ formatScore(answer.score) }}</div>
            </div>

            <div class="answer-body">
              <div class="field-block">
                <span class="field-label">文字回答</span>
                <p class="text-block">{{ answer.answer || '暂无文字回答' }}</p>
              </div>

              <div v-if="answer.audioUrl" class="field-block">
                <span class="field-label">语音回答</span>
                <audio :src="resolveMediaUrl(answer.audioUrl)" controls class="audio-player"></audio>
              </div>

              <div class="mini-grid">
                <div class="mini-card">
                  <span class="field-label">优点</span>
                  <p class="text-block">{{ getAnalysisText(answer.aiAnalysis, 'advantages') }}</p>
                </div>
                <div class="mini-card">
                  <span class="field-label">不足</span>
                  <p class="text-block">{{ getAnalysisText(answer.aiAnalysis, 'disadvantages') }}</p>
                </div>
                <div class="mini-card">
                  <span class="field-label">建议</span>
                  <p class="text-block">{{ getAnalysisText(answer.aiAnalysis, 'suggestions') }}</p>
                </div>
                <div class="mini-card">
                  <span class="field-label">培训重点</span>
                  <p class="text-block">{{ getAnalysisText(answer.aiAnalysis, 'trainingFocus') }}</p>
                </div>
              </div>

              <div v-if="getAnalysisText(answer.aiAnalysis, 'potentialPublicOpinion')" class="field-block subtle">
                <span class="field-label">舆情提示</span>
                <p class="text-block">{{ getAnalysisText(answer.aiAnalysis, 'potentialPublicOpinion') }}</p>
              </div>
            </div>
          </article>
        </section>

        <section v-if="standardAnswerGroups.length" class="section-band">
          <div class="section-head">
            <h2>标准答案参考</h2>
            <p>按视频维度展示</p>
          </div>

          <div class="standard-grid">
            <article v-for="group in standardAnswerGroups" :key="group.videoId" class="standard-card">
              <h3>{{ group.videoTitle }}</h3>
              <ol>
                <li v-for="(item, index) in group.items" :key="index">
                  {{ item.content }}
                </li>
              </ol>
            </article>
          </div>
        </section>

        <section v-if="reviewVideos.length" class="section-band">
          <div class="section-head">
            <h2>复盘视频</h2>
            <p>考试结束后可继续观看回放</p>
          </div>

          <div class="review-list">
            <button
              v-for="video in reviewVideos"
              :key="video.id"
              class="review-item"
              @click="openReviewVideo(video)"
            >
              <span class="review-title">{{ video.title }}</span>
              <span class="review-meta">{{ video.description || '点击播放' }}</span>
            </button>
          </div>
        </section>
      </template>
    </div>

    <div v-if="reviewModalVisible" class="modal-mask" @click.self="closeReviewVideo">
      <div class="modal-card">
        <div class="modal-head">
          <div>
            <h3>{{ activeReviewVideo ? activeReviewVideo.title : '复盘视频' }}</h3>
            <p>{{ activeReviewVideo && activeReviewVideo.description ? activeReviewVideo.description : '' }}</p>
          </div>
          <button class="action-btn" @click="closeReviewVideo">关闭</button>
        </div>
        <video
          v-if="activeReviewVideo"
          :src="resolveMediaUrl(activeReviewVideo.url)"
          controls
          autoplay
          class="review-player"
        ></video>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { API_BASE_URL } from '../config/api'

export default {
  name: 'ExamResult',
  data() {
    return {
      examId: null,
      loading: false,
      errorMsg: '',
      currentUser: JSON.parse(localStorage.getItem('userInfo') || '{}'),
      examInfo: {
        caseId: null,
        caseTitle: '',
        score: 0,
        status: null,
        endTime: null,
        createdTime: null
      },
      summary: {
        totalScore: 0,
        semanticScore: 0,
        keywordScore: 0,
        qualityScore: 0,
        dimensionScores: {},
        aiAvailable: true,
        advantages: '',
        disadvantages: '',
        suggestions: '',
        trainingFocus: '',
        potentialPublicOpinion: '',
        matchedKeywords: []
      },
      answers: [],
      standardAnswerGroups: [],
      reviewVideos: [],
      reviewModalVisible: false,
      activeReviewVideo: null
    }
  },
  computed: {
    matchedKeywords() {
      return this.normalizeList(this.summary.matchedKeywords)
    },
    isExamCompleted() {
      return Number(this.examInfo.status) > 0 || Boolean(this.examInfo.endTime)
    },
    dimensionEntries() {
      const entries = Object.keys(this.summary.dimensionScores || {}).map((name) => {
        return {
          name,
          score: this.toNumber(this.summary.dimensionScores[name]),
          percent: 0
        }
      })
      const maxScore = Math.max(100, ...entries.map((item) => item.score))
      return entries
        .map((item) => {
          return {
            name: item.name,
            score: item.score,
            percent: Math.min(100, Math.round((item.score / maxScore) * 100))
          }
        })
        .sort((a, b) => b.score - a.score)
    }
  },
  mounted() {
    this.examId = this.$route.query.examId ? Number(this.$route.query.examId) : null
    this.loadReport()
  },
  methods: {
    async loadReport() {
      if (!this.examId) {
        this.errorMsg = '缺少 examId 参数'
        return
      }

      this.loading = true
      this.errorMsg = ''

      try {
        const examResponse = await axios.get(`/api/exams/${this.examId}`)
        const examData = this.unwrapResponse(examResponse.data) || {}
        this.examInfo = Object.assign({}, this.examInfo, examData)

        const [summaryResponse, answersResponse] = await Promise.all([
          axios.post(`/api/ai-analysis/${this.examId}`),
          axios.get(`/api/exam-answers/exam/${this.examId}`)
        ])

        this.summary = this.normalizeSummary(this.unwrapResponse(summaryResponse.data) || {})
        this.answers = this.normalizeAnswers(this.unwrapResponse(answersResponse.data) || [])
        this.reconcileSummaryWithAnswers()
        await Promise.all([
          this.loadStandardAnswerGroups(this.answers),
          this.loadReviewVideos(this.examInfo.caseId)
        ])

        if (this.reviewVideos.length && !this.activeReviewVideo) {
          this.activeReviewVideo = this.reviewVideos[0]
        }
      } catch (error) {
        console.error('加载考试结果失败:', error)
        this.errorMsg = '加载失败：' + this.getErrorMessage(error)
      } finally {
        this.loading = false
      }
    },
    reloadReport() {
      this.loadReport()
    },
    reconcileSummaryWithAnswers() {
      const scoredAnswers = this.answers.filter((answer) => answer.score !== null && answer.score !== undefined)
      const answerTotal = scoredAnswers.reduce((sum, answer) => sum + this.toNumber(answer.score), 0)
      const storedExamScore = this.toNumber(this.examInfo.score)

      if (this.toNumber(this.summary.totalScore) <= 0) {
        this.summary.totalScore = storedExamScore > 0 ? storedExamScore : answerTotal
      }

      const analysisFields = ['semanticScore', 'keywordScore', 'qualityScore']
      analysisFields.forEach((field) => {
        if (this.toNumber(this.summary[field]) > 0) {
          return
        }
        const values = this.answers
          .map((answer) => this.toNumber(answer.aiAnalysis && answer.aiAnalysis[field]))
          .filter((value) => value > 0)
        if (values.length) {
          this.summary[field] = Math.round(values.reduce((sum, value) => sum + value, 0) / values.length)
        }
      })

      const textFields = ['advantages', 'disadvantages', 'suggestions', 'trainingFocus', 'potentialPublicOpinion']
      textFields.forEach((field) => {
        if (this.summary[field]) {
          return
        }
        const values = this.answers
          .map((answer) => this.getAnalysisText(answer.aiAnalysis, field))
          .filter(Boolean)
        if (values.length) {
          this.summary[field] = values.join('；')
        }
      })

      if (!this.summary.matchedKeywords.length) {
        const keywords = []
        this.answers.forEach((answer) => {
          this.normalizeList(answer.aiAnalysis && answer.aiAnalysis.matchedKeywords).forEach((keyword) => {
            if (!keywords.includes(keyword)) {
              keywords.push(keyword)
            }
          })
        })
        this.summary.matchedKeywords = keywords
      }
    },
    normalizeSummary(summary) {
      let dimensionScores = {}
      if (summary.dimensionScores && typeof summary.dimensionScores === 'object' && !Array.isArray(summary.dimensionScores)) {
        dimensionScores = summary.dimensionScores
      } else if (typeof summary.dimensionScores === 'string') {
        try {
          dimensionScores = JSON.parse(summary.dimensionScores)
        } catch (e) {
          dimensionScores = {}
        }
      }

      return {
        totalScore: this.toNumber(summary.totalScore || summary.score || this.examInfo.score || 0),
        semanticScore: this.toNumber(summary.semanticScore),
        keywordScore: this.toNumber(summary.keywordScore),
        qualityScore: this.toNumber(summary.qualityScore),
        dimensionScores,
        aiAvailable: summary.aiAvailable !== false,
        advantages: summary.advantages || '',
        disadvantages: summary.disadvantages || '',
        suggestions: summary.suggestions || '',
        trainingFocus: summary.trainingFocus || '',
        potentialPublicOpinion: summary.potentialPublicOpinion || '',
        matchedKeywords: this.normalizeList(summary.matchedKeywords)
      }
    },
    normalizeAnswers(list) {
      return list.map((item, index) => {
        return {
          id: item.id || `${index}-${item.videoId || 'video'}`,
          index: index + 1,
          examId: item.examId,
          videoId: item.videoId,
          videoTitle: item.videoTitle || '',
          answer: item.answer || '',
          audioUrl: item.audioUrl || '',
          score: item.score !== null && item.score !== undefined ? this.toNumber(item.score) : null,
          aiAnalysis: item.aiAnalysis || {}
        }
      })
    },
    async loadStandardAnswerGroups(answers) {
      const seen = new Set()
      const tasks = []

      answers.forEach((answer) => {
        if (!answer.videoId || seen.has(answer.videoId)) {
          return
        }
        seen.add(answer.videoId)
        tasks.push(
          axios.get(`/api/videos/${answer.videoId}/answer-keys`)
            .then((response) => {
              const items = this.unwrapResponse(response.data) || []
              if (!items.length) {
                return null
              }
              return {
                videoId: answer.videoId,
                videoTitle: answer.videoTitle || `视频 ${answer.videoId}`,
                items
              }
            })
            .catch((error) => {
              console.error('加载标准答案失败:', error)
              return null
            })
        )
      })

      const groups = await Promise.all(tasks)
      this.standardAnswerGroups = groups.filter(Boolean)
    },
    async loadReviewVideos(caseId) {
      if (!caseId) {
        this.reviewVideos = []
        return
      }

      try {
        const response = await axios.get(`/api/videos/case/${caseId}/review`)
        this.reviewVideos = this.unwrapResponse(response.data) || []
      } catch (error) {
        console.error('加载复盘视频失败:', error)
        this.reviewVideos = []
      }
    },
    openReviewVideo(video) {
      const targetVideo = video || this.reviewVideos[0]
      if (!targetVideo) {
        return
      }
      this.activeReviewVideo = targetVideo
      this.reviewModalVisible = true
    },
    closeReviewVideo() {
      this.reviewModalVisible = false
    },
    downloadReport() {
      window.print()
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1)
        return
      }
      this.$router.push('/academy')
    },
    unwrapResponse(payload) {
      if (payload === null || payload === undefined) {
        return null
      }
      if (Array.isArray(payload)) {
        return payload
      }
      if (payload && typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'code') && Object.prototype.hasOwnProperty.call(payload, 'data')) {
        return payload.data
      }
      return payload
    },
    normalizeList(value) {
      if (!value) {
        return []
      }
      if (Array.isArray(value)) {
        return value.filter(Boolean).map((item) => String(item))
      }
      if (typeof value === 'string') {
        return value
          .split(/[,，;；\n]/)
          .map((item) => item.trim())
          .filter(Boolean)
      }
      return [String(value)]
    },
    getAnalysisText(analysis, field) {
      if (!analysis) {
        return ''
      }
      const value = analysis[field]
      if (Array.isArray(value)) {
        return value.join('；')
      }
      return value || ''
    },
    formatScore(score) {
      if (score === null || score === undefined || score === '') {
        return '未评分'
      }
      return `${score} 分`
    },
    formatTime(value) {
      if (!value) {
        return '-'
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return String(value)
      }
      return date.toLocaleString('zh-CN')
    },
    resolveMediaUrl(url) {
      if (!url) {
        return ''
      }
      if (/^https?:\/\//i.test(url)) {
        return url
      }
      return `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`
    },
    toNumber(value, fallback) {
      const parsed = Number(value)
      if (Number.isNaN(parsed)) {
        return fallback === undefined ? 0 : fallback
      }
      return parsed
    },
    getErrorMessage(error) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : (error && error.message ? error.message : '未知错误')
    }
  }
}
</script>

<style scoped>
.report-page {
  min-height: 100vh;
  background: #f4f7fb;
  color: #10233a;
}

.page-shell {
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
  padding: 24px 20px 48px;
}

.page-header,
.section-band,
.state-panel {
  width: 100%;
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-end;
  padding: 24px;
  margin-bottom: 20px;
  background: #fff;
  border: 1px solid #e4ebf3;
  border-radius: 12px;
}

.eyebrow {
  margin: 0 0 8px;
  font-size: 12px;
  letter-spacing: 0.08em;
  color: #4c6ef5;
  text-transform: uppercase;
}

.page-header h1,
.section-head h2,
.info-card h2,
.analysis-card h2,
.standard-card h3,
.answer-head h3,
.modal-head h3 {
  margin: 0;
}

.header-subtitle {
  margin: 8px 0 0;
  color: #5b6b7d;
}

.header-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.action-btn {
  border: 1px solid #ced8e3;
  background: #fff;
  color: #203040;
  border-radius: 10px;
  padding: 10px 16px;
  cursor: pointer;
  transition: 0.2s ease;
}

.action-btn:hover:not(:disabled) {
  border-color: #4c6ef5;
  color: #4c6ef5;
}

.action-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.action-btn.primary {
  background: #4c6ef5;
  border-color: #4c6ef5;
  color: #fff;
}

.action-btn.primary:hover:not(:disabled) {
  background: #3b5bdb;
  color: #fff;
}

.state-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  min-height: 240px;
  background: #fff;
  border: 1px solid #e4ebf3;
  border-radius: 12px;
  margin-bottom: 20px;
}

.state-panel.compact {
  min-height: 120px;
}

.state-error {
  color: #b42318;
}

.spinner {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid rgba(76, 110, 245, 0.2);
  border-top-color: #4c6ef5;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.section-band {
  margin-bottom: 20px;
}

.summary-grid,
.meta-grid,
.analysis-grid,
.standard-grid {
  display: grid;
  gap: 16px;
}

.summary-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.meta-grid {
  grid-template-columns: 1.3fr 1fr;
}

.analysis-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.standard-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.summary-card,
.info-card,
.analysis-card,
.standard-card,
.answer-card {
  background: #fff;
  border: 1px solid #e4ebf3;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(16, 35, 58, 0.04);
}

.summary-card {
  padding: 20px;
  min-height: 140px;
}

.summary-card.score-card {
  background: linear-gradient(135deg, #4c6ef5, #5f3dc4);
  color: #fff;
}

.card-label {
  display: block;
  font-size: 13px;
  color: inherit;
  opacity: 0.8;
}

.score-value {
  margin-top: 18px;
  font-size: 46px;
  font-weight: 700;
  line-height: 1;
}

.metric-value {
  margin-top: 18px;
  font-size: 34px;
  font-weight: 700;
  color: #1f4bd8;
  line-height: 1;
}

.card-note {
  margin: 12px 0 0;
  color: inherit;
  opacity: 0.75;
  font-size: 13px;
}

.info-card,
.analysis-card,
.standard-card {
  padding: 20px;
}

.info-list {
  margin: 16px 0 0;
}

.info-list div {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px dashed #e6edf5;
}

.info-list div:last-child {
  border-bottom: 0;
}

.info-list dt {
  color: #728196;
}

.info-list dd {
  margin: 0;
  text-align: right;
}

.tag-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

.tag {
  display: inline-flex;
  align-items: center;
  padding: 7px 12px;
  background: #edf2ff;
  color: #2f4ed7;
  border-radius: 999px;
  font-size: 13px;
}

.empty-text {
  margin: 16px 0 0;
  color: #728196;
}

.section-head {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-head p {
  margin: 0;
  color: #728196;
}

.analysis-grid {
  margin-bottom: 16px;
}

.analysis-card h2,
.info-card h2,
.standard-card h3 {
  font-size: 18px;
}

.text-block {
  margin: 14px 0 0;
  white-space: pre-wrap;
  line-height: 1.7;
  color: #344256;
}

.single-card {
  margin-top: 16px;
}

.dimension-list {
  display: grid;
  gap: 14px;
  background: #fff;
  border: 1px solid #e4ebf3;
  border-radius: 12px;
  padding: 20px;
}

.dimension-top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 14px;
}

.dimension-bar {
  height: 10px;
  border-radius: 999px;
  background: #e9eef6;
  overflow: hidden;
}

.dimension-bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #4c6ef5, #2f9e44);
}

.answer-card {
  padding: 20px;
  margin-bottom: 16px;
}

.answer-head {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-start;
  padding-bottom: 14px;
  border-bottom: 1px solid #eef3f8;
}

.answer-head p {
  margin: 6px 0 0;
  color: #728196;
}

.answer-score {
  white-space: nowrap;
  padding: 8px 12px;
  border-radius: 999px;
  background: #edf2ff;
  color: #2f4ed7;
  font-weight: 600;
}

.answer-body {
  padding-top: 16px;
}

.field-block {
  margin-bottom: 16px;
}

.field-block.subtle {
  padding: 14px 16px;
  border-radius: 10px;
  background: #f8fbff;
  border: 1px solid #e8eef7;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  color: #728196;
}

.audio-player {
  width: 100%;
}

.mini-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.mini-card {
  padding: 14px 16px;
  background: #f8fbff;
  border: 1px solid #e8eef7;
  border-radius: 10px;
}

.standard-card ol {
  margin: 14px 0 0;
  padding-left: 20px;
  color: #344256;
}

.standard-card li + li {
  margin-top: 10px;
}

.review-list {
  display: grid;
  gap: 12px;
}

.review-item {
  text-align: left;
  width: 100%;
  border: 1px solid #e4ebf3;
  background: #fff;
  border-radius: 12px;
  padding: 16px 18px;
  cursor: pointer;
}

.review-title {
  display: block;
  font-weight: 600;
}

.review-meta {
  display: block;
  margin-top: 6px;
  color: #728196;
  font-size: 13px;
}

.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(10, 20, 35, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 50;
}

.modal-card {
  width: min(960px, 100%);
  background: #fff;
  border-radius: 14px;
  padding: 20px;
}

.modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.modal-head p {
  margin: 6px 0 0;
  color: #728196;
}

.review-player {
  width: 100%;
  max-height: 70vh;
  background: #000;
  border-radius: 12px;
}

@media (max-width: 1100px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .meta-grid,
  .analysis-grid,
  .standard-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .page-shell {
    padding: 16px 12px 32px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .summary-grid,
  .mini-grid {
    grid-template-columns: 1fr;
  }

  .answer-head,
  .section-head {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media print {
  .header-actions,
  .modal-mask {
    display: none !important;
  }

  .report-page {
    background: #fff;
  }
}
</style>
